import { useSyncExternalStore } from "react";

/** Shared localStorage key used independently on each app origin. */
const AUTH_STORAGE_KEY = "nazare-session";

/**
 * Shape of the auth session persisted by each app's auth bootstrap.
 *
 * This is intentionally loose (`user: unknown`) — this package shouldn't
 * depend on nazare-frontend's app-specific `User` type, and consumers
 * should narrow/validate `user` themselves if they need its shape.
 */
export type StoredAuth = {
  accessToken?: string;
  refreshToken?: string;
  expiresAt?: number;
  user?: unknown;
};

// Module-level cache so repeated reads of an unchanged localStorage value
// return the *same* object reference. This isn't just an optimization: it's
// required for `useStoredAuth`'s use of `useSyncExternalStore` below, whose
// snapshot must be referentially stable when nothing has changed, or React
// treats every render as a fresh change and re-renders in a loop.
let cachedRaw: string | null = null;
let cachedValue: StoredAuth | null = null;

function readStoredAuth(): StoredAuth | null {
  if (typeof window === "undefined") return null;

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
  } catch {
    // localStorage can throw (e.g. disabled/blocked storage access) — treat
    // as "not logged in" rather than surfacing an error.
    raw = null;
  }

  if (raw === cachedRaw) {
    return cachedValue;
  }
  cachedRaw = raw;

  if (!raw) {
    cachedValue = null;
    return cachedValue;
  }

  try {
    const parsed: unknown = JSON.parse(raw);
    cachedValue =
      parsed && typeof parsed === "object" ? (parsed as StoredAuth) : null;
  } catch {
    cachedValue = null;
  }

  return cachedValue;
}

/**
 * Reads the auth session persisted on the current app origin at
 * `localStorage["nazare-session"]`.
 *
 * Defensive by design — returns `null` if the key is missing, empty, or
 * fails to parse as JSON, rather than throwing.
 */
export function getStoredAuth(): StoredAuth | null {
  return readStoredAuth();
}

function subscribe(onStoreChange: () => void): () => void {
  if (typeof window === "undefined") return () => {};

  // Only fires when *another* tab/window writes localStorage — a same-tab
  // write doesn't dispatch "storage". That's fine here: this app never
  // writes from another tab are the only changes this read-only helper needs
  // to subscribe to; an app bootstrap writes before its consumers render.
  window.addEventListener("storage", onStoreChange);
  return () => window.removeEventListener("storage", onStoreChange);
}

function getServerSnapshot(): StoredAuth | null {
  return null;
}

/**
 * React hook version of {@link getStoredAuth} — returns the same shape, and
 * re-reads it when another tab writes (or clears) the "nazare-session" key
 * so a login/logout in the main app is reflected without a manual refresh.
 */
export function useStoredAuth(): StoredAuth | null {
  return useSyncExternalStore(subscribe, readStoredAuth, getServerSnapshot);
}

/**
 * Whether a {@link StoredAuth} session's access token has (or should be
 * treated as having) already expired.
 *
 * The current auth singleton persists `expiresAt` as Unix epoch milliseconds.
 * A session migrated from the legacy store can retain epoch seconds until its
 * first token refresh, so both representations are accepted here.
 */
export function isStoredAuthExpired(
  auth: StoredAuth | null | undefined,
): boolean {
  // No expiry info at all: nothing to compare against, so don't assume
  // expired (callers still gate on token *presence* separately).
  if (!auth?.expiresAt) return false;
  const expiresAt =
    auth.expiresAt < 1e12 ? auth.expiresAt * 1000 : auth.expiresAt;
  return expiresAt <= Date.now();
}
